/**
 * @file src/room/chatClient.js
 * @description Room chat plumbing — no UI (the panel design is deferred). Sends `chat:message`
 * over the room socket, keeps an ordered store of the stamped messages the relay broadcasts
 * (backlog included), tracks per-message delivery, and applies a local mute set. The relay is the
 * authority: it stamps sender + seq + serverTs, and its echo back to the sender IS the delivery
 * receipt. A `room:error` carrying our msgId (rate limit, no display name) marks that message
 * failed, as does a timeout with no echo.
 *
 * Ordering: by relay `seq` (one counter per room), pending locals last in send order. Dedupe is by
 * `msgId` — a backlog replay after a reconnect can't double-insert, and the echo of our own send
 * upgrades the pending entry in place.
 *
 * Moderation seams:
 *  - `muteClient`/`unmuteClient` — a LOCAL mute set (clientIds); muted senders' messages are kept
 *    but filtered out of `getMessages()`.
 *  - `applyBlockSet` — account-level blocks (backend-driven, keyed by USER id — blocks only exist
 *    for logged-in senders; guests are covered by mute). Blocked senders' messages are dropped on
 *    receive and filtered out of reads.
 */

import { randomMsgId } from './wire.js';

export const CHAT_MAX_LENGTH = 500;
/** No relay echo within this window → the send is presumed lost. */
export const CHAT_DELIVERY_TIMEOUT_MS = 10_000;

/**
 * @typedef {Object} ChatMessage
 * @property {string} msgId
 * @property {string} text
 * @property {{ clientId: string, displayName?: string|null, userId?: string|null }} from
 * @property {number|null} seq relay order (null while pending)
 * @property {number|null} serverTs relay stamp (null while pending)
 * @property {'pending'|'delivered'|'failed'} status own messages; remote messages are 'delivered'
 */

/**
 * @param {object} deps
 * @param {(msg: object) => void} deps.sendRaw — the room socket's raw send (buffered until joined).
 * @param {string} deps.roomId
 * @param {() => string} deps.getClientId — own identity, read live (echo detection, self-mute
 *   guard). A thunk because a failed seat reclaim can switch the effective clientId mid-session.
 * @param {() => number} [deps.now]
 * @param {typeof setTimeout} [deps.schedule] injectable timer (tests).
 * @param {typeof clearTimeout} [deps.cancel]
 */
export function createChatClient({
  sendRaw,
  roomId,
  getClientId,
  now = () => Date.now(),
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (h) => clearTimeout(h),
}) {
  /** @type {Map<string, ChatMessage>} msgId → message (the one store; order derived on read). */
  const byId = new Map();
  /** msgId → pending delivery timer. */
  const timers = new Map();
  const muted = new Set();
  /** Blocked USER ids (account-level; guests have none and can only be muted). */
  let blockedUserIds = new Set();
  const listeners = new Set();
  const messageListeners = new Set();

  function notify() {
    for (const fn of listeners) {
      try {
        fn();
      } catch (error) {
        console.warn('[room chat] listener failed', error);
      }
    }
  }

  /** Tell message listeners a NEW relay message was inserted (never the echo of a pending send). */
  function announceMessage(message, meta) {
    for (const fn of messageListeners) {
      try {
        fn(message, meta);
      } catch (error) {
        console.warn('[room chat] message listener failed', error);
      }
    }
  }

  function isBlockedSender(from) {
    const userId = from?.userId;
    return typeof userId === 'string' && userId.length > 0 && blockedUserIds.has(userId);
  }

  function orderKey(message) {
    // Relay-stamped messages order by seq; pending locals sort after everything stamped, by
    // send time (serverTs is null until the echo lands).
    return message.seq !== null ? message.seq : Number.MAX_SAFE_INTEGER;
  }

  function settleTimer(msgId) {
    const timer = timers.get(msgId);
    if (timer !== undefined) {
      try {
        cancel(timer);
      } catch {
        /* already fired */
      }
      timers.delete(msgId);
    }
  }

  function markFailed(msgId) {
    settleTimer(msgId);
    const message = byId.get(msgId);
    if (message && message.status === 'pending') {
      message.status = 'failed';
      notify();
    }
  }

  return {
    /**
     * Send one chat message. Returns the local msgId (track it in UI), or null when the text is
     * empty/too long after trimming.
     */
    send(text) {
      const trimmed = typeof text === 'string' ? text.trim() : '';
      if (trimmed.length === 0 || trimmed.length > CHAT_MAX_LENGTH) return null;
      const msgId = randomMsgId();
      byId.set(msgId, {
        msgId,
        text: trimmed,
        from: { clientId: getClientId() },
        seq: null,
        serverTs: now(),
        status: 'pending',
      });
      timers.set(msgId, schedule(() => markFailed(msgId), CHAT_DELIVERY_TIMEOUT_MS));
      sendRaw({ type: 'chat:message', roomId, msgId, text: trimmed });
      notify();
      return msgId;
    },

    /**
     * A stamped `chat:message` from the relay (live broadcast — echo of our own send included).
     * `backlog: true` marks a ring-buffer replay (join/reconnect context, not a live arrival) so
     * message listeners can count it as unseen without treating it as breaking news.
     */
    receive(msg, { backlog = false } = {}) {
      if (!msg?.msgId || typeof msg.text !== 'string') return;
      if (isBlockedSender(msg.from)) return; // account-level block: dropped outright
      const existing = byId.get(msg.msgId);
      if (existing) {
        // The echo of our own send (or a backlog duplicate): adopt the relay stamps in place.
        settleTimer(msg.msgId);
        existing.seq = Number.isFinite(msg.seq) ? msg.seq : existing.seq;
        existing.serverTs = Number.isFinite(msg.serverTs) ? msg.serverTs : existing.serverTs;
        if (msg.from) existing.from = msg.from;
        existing.status = 'delivered';
        notify();
        return;
      }
      const message = {
        msgId: msg.msgId,
        text: msg.text,
        from: msg.from ?? { clientId: null },
        seq: Number.isFinite(msg.seq) ? msg.seq : null,
        serverTs: Number.isFinite(msg.serverTs) ? msg.serverTs : null,
        status: 'delivered',
      };
      byId.set(msg.msgId, message);
      notify();
      announceMessage(message, { backlog });
    },

    /** The room's ring-buffer backlog from `room:joined` (already stamped; dedupes by msgId). */
    applyBacklog(messages) {
      if (!Array.isArray(messages)) return;
      for (const msg of messages) this.receive(msg, { backlog: true });
    },

    /** Re-send a FAILED message (same msgId, so the relay's echo still matches it). */
    retry(msgId) {
      const message = byId.get(msgId);
      if (!message || message.status !== 'failed') return false;
      message.status = 'pending';
      message.serverTs = now();
      settleTimer(msgId);
      timers.set(msgId, schedule(() => markFailed(msgId), CHAT_DELIVERY_TIMEOUT_MS));
      sendRaw({ type: 'chat:message', roomId, msgId, text: message.text });
      notify();
      return true;
    },

    /** A relay refusal carrying one of our msgIds (rate limit, missing display name…). */
    handleError(error) {
      if (error?.msgId && byId.has(error.msgId)) markFailed(error.msgId);
    },

    /** Ordered messages, muted senders filtered (pass includeMuted to see them anyway). */
    getMessages({ includeMuted = false } = {}) {
      const all = [...byId.values()].sort((a, b) => {
        const d = orderKey(a) - orderKey(b);
        return d !== 0 ? d : (a.serverTs ?? 0) - (b.serverTs ?? 0);
      });
      const visible = all.filter((m) => !isBlockedSender(m.from));
      if (includeMuted) return visible;
      return visible.filter((m) => {
        const id = m.from?.clientId;
        return !id || !muted.has(id);
      });
    },

    // ── local mute (this device only) ──
    muteClient(id) {
      if (id && id !== getClientId()) {
        muted.add(id);
        notify();
      }
    },
    unmuteClient(id) {
      if (muted.delete(id)) notify();
    },
    isMuted(id) {
      return muted.has(id);
    },

    /**
     * Account-level block hook: replace the viewer's blocked USER id set (fetched from the
     * backend). Blocked senders' future messages are dropped on receive; their existing messages
     * are filtered out of reads from here on. Guests carry no userId, so they never match — local
     * mute covers them.
     */
    applyBlockSet(userIds) {
      blockedUserIds = new Set(Array.isArray(userIds) ? userIds.filter(Boolean) : []);
      notify();
    },

    /** Is this sender userId in the viewer's block set right now? */
    isBlockedUser(userId) {
      return typeof userId === 'string' && blockedUserIds.has(userId);
    },

    /** Subscribe to store changes. Returns an unsubscribe fn. */
    subscribe(fn) {
      if (typeof fn !== 'function') return () => {};
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    /**
     * Subscribe to NEW relay messages as they insert (never the echo upgrade of an own pending
     * send). Listener gets `(message, { backlog })` — backlog marks a ring-buffer replay. Blocked
     * senders never announce (dropped before insert); muted senders DO (check `isMuted`).
     * Returns an unsubscribe fn.
     */
    onMessage(fn) {
      if (typeof fn !== 'function') return () => {};
      messageListeners.add(fn);
      return () => messageListeners.delete(fn);
    },

    dispose() {
      for (const msgId of [...timers.keys()]) settleTimer(msgId);
      listeners.clear();
      messageListeners.clear();
    },
  };
}
